import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, AlertCircle, Heart, Phone, Globe, Users, CheckCircle } from 'lucide-react';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';

const ResponsibleGaming = () => {
  // Structured Data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Responsible Gaming Guide - Bet Safely with AI Guidance",
    "description": "Comprehensive guide to responsible sports betting. Learn about setting limits, recognizing warning signs, and getting help for problem gambling.",
    "author": {
      "@type": "Organization",
      "name": "PickStar AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStar AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
  "datePublished": "2025-08-22",
  "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/resources/responsible-gaming"
    },
    "image": "https://pickstarai.com/og-responsible-gaming.jpg"
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What are the warning signs of problem gambling?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Warning signs include spending more time or money than intended, lying about betting activities, neglecting responsibilities, betting to escape problems, feeling anxious when not betting, and unsuccessful attempts to stop."
        }
      },
      {
        "@type": "Question",
        "name": "How can I set limits on my betting?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Set personal boundaries including time limits (daily/weekly betting time), loss limits (maximum you can afford to lose), deposit limits (control account funding), and bet size limits (never bet more than 1-3% of bankroll)."
        }
      },
      {
        "@type": "Question",
        "name": "Where can I get help for problem gambling?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "In the US, call the National Problem Gambling Helpline at 1-800-522-4700 or visit ncpgambling.org. International resources include GamCare (UK), Gambling Help (Australia), and ConnexOntario (Canada). Support is available 24/7."
        }
      },
      {
        "@type": "Question",
        "name": "How does PickStar AI promote responsible gaming?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "PickStar AI promotes responsible gaming through data-driven decisions that remove emotion, bankroll management tools, educational content about probability, and a no-pressure environment where betting decisions remain yours."
        }
      }
    ]
  };

  const medicalWebPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "Problem Gambling Resources and Support",
    "about": {
      "@type": "MedicalCondition",
      "name": "Gambling Disorder",
      "alternateName": "Problem Gambling"
    },
    "aspect": "Prevention",
    "audience": {
      "@type": "PeopleAudience",
      "suggestedGender": "unisex",
      "suggestedMinAge": 18
    },
    "lastReviewed": new Date().toISOString()
  };

  return (
    <>
      <SEOHead 
        title="Responsible Gaming Guide - Bet Safely with AI | PickStar AI"
        description="Learn about responsible sports betting practices. Get help with problem gambling, set betting limits, recognize warning signs, and access 24/7 support resources."
        keywords="responsible gaming, problem gambling help, betting limits, gambling addiction, safe betting, gambling support, betting responsibly, gambling helpline"
        canonical="https://pickstarai.com/resources/responsible-gaming"
        ogImage="https://pickstarai.com/og-responsible-gaming.jpg"
        structuredData={[articleStructuredData, faqStructuredData, medicalWebPageStructuredData]}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        {/* Header */}
        <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <nav className="max-w-4xl mx-auto px-4 py-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
              aria-label="Return to PickStar AI homepage"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
          </nav>
        </header>

        {/* Hero Section */}
        <section className="py-8 sm:py-12 md:py-16 px-4" aria-labelledby="hero-heading">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-emerald-400" aria-hidden="true" />
              <h1 id="hero-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
                Responsible Gaming
              </h1>
            </div>
            <p className="text-lg sm:text-xl text-gray-300 mb-8">
              Your wellbeing comes first. Bet smart, bet safe, bet responsibly.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <main className="px-4 pb-16">
          <div className="max-w-4xl mx-auto space-y-12">
            
            {/* Our Commitment */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="commitment-heading">
              <h2 id="commitment-heading" className="text-xl sm:text-2xl font-bold text-white mb-4">
                Our Commitment to Responsible Gaming
              </h2>
              <p className="text-gray-300 mb-4">
                At PickStar AI, we believe that sports betting should be an entertaining and enjoyable activity. 
                We're committed to promoting responsible gaming practices and providing tools to help our users 
                maintain control over their betting activities.
              </p>
              <p className="text-gray-300">
                Our AI technology is designed not just to improve your betting success, but also to encourage 
                smart, informed decisions based on data rather than emotion or impulse.
              </p>
            </section>

            {/* Know Your Limits */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="limits-heading">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-6 h-6 text-emerald-400" aria-hidden="true" />
                <h2 id="limits-heading" className="text-xl sm:text-2xl font-bold text-white">Know Your Limits</h2>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 rounded-lg p-4 sm:p-6 border border-emerald-500/20">
                  <h3 className="text-lg font-semibold text-white mb-3">Set Personal Boundaries</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <div>
                          <p className="text-white font-medium">Time Limits</p>
                          <p className="text-gray-300 text-sm">Decide how much time per day/week to spend betting</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <div>
                          <p className="text-white font-medium">Loss Limits</p>
                          <p className="text-gray-300 text-sm">Set a maximum amount you can afford to lose</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <div>
                          <p className="text-white font-medium">Deposit Limits</p>
                          <p className="text-gray-300 text-sm">Control how much money you add to betting accounts</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <div>
                          <p className="text-white font-medium">Bet Size Limits</p>
                          <p className="text-gray-300 text-sm">Never bet more than 1-3% of your bankroll</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <article className="bg-black/30 rounded-lg p-4 sm:p-6 border border-white/10">
                  <h3 className="text-lg font-semibold text-white mb-3">Golden Rules</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400" aria-hidden="true">•</span>
                      <span>Never bet money you can't afford to lose</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400" aria-hidden="true">•</span>
                      <span>Don't bet to recover losses (chasing)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400" aria-hidden="true">•</span>
                      <span>Never borrow money to gamble</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400" aria-hidden="true">•</span>
                      <span>Don't bet under the influence of alcohol or drugs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400" aria-hidden="true">•</span>
                      <span>Take regular breaks from betting</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400" aria-hidden="true">•</span>
                      <span>View betting as entertainment, not a way to make money</span>
                    </li>
                  </ul>
                </article>
              </div>
            </section>

            {/* Warning Signs */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="warning-heading">
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle className="w-6 h-6 text-amber-400" aria-hidden="true" />
                <h2 id="warning-heading" className="text-xl sm:text-2xl font-bold text-white">
                  Recognize the Warning Signs
                </h2>
              </div>
              
              <p className="text-gray-300 mb-6">
                Problem gambling can develop gradually. Be aware of these warning signs:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <article className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
                  <h3 className="font-semibold text-red-400 mb-2">Behavioral Signs</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Spending more time or money than intended</li>
                    <li>• Lying about betting activities</li>
                    <li>• Neglecting responsibilities to bet</li>
                    <li>• Betting to escape problems or emotions</li>
                    <li>• Unsuccessful attempts to stop or reduce betting</li>
                  </ul>
                </article>
                
                <article className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
                  <h3 className="font-semibold text-red-400 mb-2">Emotional Signs</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Feeling anxious when not betting</li>
                    <li>• Mood swings related to wins/losses</li>
                    <li>• Depression or irritability</li>
                    <li>• Loss of interest in other activities</li>
                    <li>• Feelings of guilt or shame about betting</li>
                  </ul>
                </article>
              </div>
              
              <div className="mt-6 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30" role="alert">
                <p className="text-amber-300 text-sm">
                  <strong>Important:</strong> If you recognize any of these signs in yourself or someone you know, 
                  it's important to seek help immediately. Problem gambling is treatable, and support is available.
                </p>
              </div>
            </section>

            {/* How PickStar AI Helps */}
            <section className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-emerald-500/20" aria-labelledby="ai-helps-heading">
              <h2 id="ai-helps-heading" className="text-xl sm:text-2xl font-bold text-white mb-6">
                How PickStar AI Promotes Responsible Gaming
              </h2>
              
              <div className="space-y-4">
                <article className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Data-Driven Decisions</h3>
                  <p className="text-gray-300 text-sm">
                    Our AI removes emotion from betting decisions, helping you make rational choices based on 
                    statistics and probability rather than gut feelings or desperation.
                  </p>
                </article>
                
                <article className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Bankroll Management Tools</h3>
                  <p className="text-gray-300 text-sm">
                    We provide guidance on proper bet sizing and bankroll management to help you stay within 
                    your limits and avoid catastrophic losses.
                  </p>
                </article>
                
                <article className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Educational Content</h3>
                  <p className="text-gray-300 text-sm">
                    Our platform educates users about probability, expected value, and the realities of sports 
                    betting to set proper expectations.
                  </p>
                </article>
                
                <article className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">No Pressure Environment</h3>
                  <p className="text-gray-300 text-sm">
                    We never pressure users to bet. Our AI provides analysis and predictions, but the decision 
                    to bet always remains yours.
                  </p>
                </article>
              </div>
            </section>

            {/* Self-Assessment */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="assessment-heading">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-emerald-400" aria-hidden="true" />
                <h2 id="assessment-heading" className="text-xl sm:text-2xl font-bold text-white">
                  Self-Assessment Questions
                </h2>
              </div>
              
              <p className="text-gray-300 mb-6">
                Ask yourself these questions honestly to assess your relationship with betting:
              </p>
              
              <ol className="space-y-3" role="list">
                <li className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Do you bet more than you can afford to lose?</p>
                </li>
                <li className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Have you ever lied to family or friends about your betting?</p>
                </li>
                <li className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Do you feel the need to bet with larger amounts to get excitement?</p>
                </li>
                <li className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Have you tried to cut down on betting but couldn't?</p>
                </li>
                <li className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Do you bet to escape worry, boredom, or depression?</p>
                </li>
              </ol>
              
              <p className="text-amber-300 mt-6 text-sm" role="alert">
                If you answered "yes" to any of these questions, you may want to evaluate your betting habits 
                and consider seeking support.
              </p>
            </section>

            {/* Getting Help */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="help-heading">
              <div className="flex items-center gap-3 mb-6">
                <Phone className="w-6 h-6 text-emerald-400" aria-hidden="true" />
                <h2 id="help-heading" className="text-xl sm:text-2xl font-bold text-white">
                  Getting Help & Support
                </h2>
              </div>
              
              <p className="text-gray-300 mb-6">
                If you or someone you know needs help with problem gambling, these resources are available 24/7:
              </p>
              
              <div className="space-y-4">
                <article className="bg-emerald-500/10 rounded-lg p-4 sm:p-6 border border-emerald-500/30">
                  <h3 className="font-semibold text-white mb-3">United States</h3>
                  <address className="space-y-2 text-gray-300 not-italic">
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-400 flex-shrink-0" aria-hidden="true" />
                      <strong>National Problem Gambling Helpline:</strong> 
                      <a href="tel:1-800-522-4700" className="text-emerald-400 hover:text-emerald-300">
                        1-800-522-4700
                      </a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-emerald-400 flex-shrink-0" aria-hidden="true" />
                      <strong>Website:</strong>{' '}
                      <a href="https://www.ncpgambling.org" target="_blank" rel="noopener noreferrer" 
                         className="text-emerald-400 hover:text-emerald-300">
                        www.ncpgambling.org
                      </a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-400 flex-shrink-0" aria-hidden="true" />
                      <strong>Gamblers Anonymous:</strong>{' '}
                      <a href="https://www.gamblersanonymous.org" target="_blank" rel="noopener noreferrer"
                         className="text-emerald-400 hover:text-emerald-300">
                        www.gamblersanonymous.org
                      </a>
                    </p>
                  </address>
                </article>
                
                <article className="bg-violet-500/10 rounded-lg p-4 sm:p-6 border border-violet-500/30">
                  <h3 className="font-semibold text-white mb-3">International Resources</h3>
                  <address className="space-y-2 text-gray-300 not-italic">
                    <p><strong>UK:</strong> GamCare - 0808 8020 133</p>
                    <p><strong>Australia:</strong> Gambling Help - 1800 858 858</p>
                    <p><strong>Canada:</strong> ConnexOntario - 1-866-531-2600</p>
                    <p><strong>Brazil:</strong> Jogadores Anônimos - www.jogadoresanonimos.org.br</p>
                  </address>
                </article>
              </div>
            </section>

            {/* Tools and Resources */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 sm:p-8 border border-white/10" aria-labelledby="tools-heading">
              <h2 id="tools-heading" className="text-xl sm:text-2xl font-bold text-white mb-6">
                Helpful Tools & Resources
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <article className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Self-Exclusion Programs</h3>
                  <p className="text-gray-300 text-sm">
                    Most betting sites offer self-exclusion options to temporarily or permanently block access 
                    to your account.
                  </p>
                </article>
                
                <article className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Blocking Software</h3>
                  <p className="text-gray-300 text-sm">
                    Software like GamBlock or Betfilter can block access to gambling websites on your devices.
                  </p>
                </article>
                
                <article className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Financial Controls</h3>
                  <p className="text-gray-300 text-sm">
                    Ask your bank about gambling transaction blocks or have someone you trust manage your finances.
                  </p>
                </article>
                
                <article className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Support Groups</h3>
                  <p className="text-gray-300 text-sm">
                    Join local or online support groups to connect with others who understand your struggle.
                  </p>
                </article>
              </div>
            </section>

            {/* Final Message */}
            <section className="text-center py-6 sm:py-8 bg-gradient-to-r from-emerald-500/10 to-violet-500/10 rounded-xl p-6 sm:p-8 border border-emerald-500/20" aria-labelledby="final-heading">
              <h2 id="final-heading" className="text-xl sm:text-2xl font-bold text-white mb-4">
                Remember: Your Wellbeing Comes First
              </h2>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Sports betting should enhance your enjoyment of sports, not harm your life. If it stops being 
                fun, it's time to take a break. We're here to provide intelligent analysis, but your health, 
                relationships, and financial security should always be your top priority.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all border border-white/20"
                  aria-label="Return to PickStar AI homepage"
                >
                  Return Home
                </Link>
                <a 
                  href="https://www.ncpgambling.org/help-treatment/help-by-state/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-600 transition-all"
                  aria-label="Find gambling help resources near your location"
                >
                  Find Help Near You
                </a>
              </div>
            </section>
          </div>
        </main>

        {/* Footer */}
        <SEOFooter language="en" />
      </div>

      {/* Structured Data Scripts */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(medicalWebPageStructuredData) }}
      />
    </>
  );
};

export default ResponsibleGaming;